import type { IconName } from '@/types/icon-names'
import type { CountryCode, CountryMasks, Operators, TranslateFunction } from '@/types/types.ts'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'
import utc from 'dayjs/plugin/utc'
import { useCookies } from 'vue3-cookies'
import { useI18n } from 'vue-i18n'
import { countryMasks, operatorsData } from '@/constants/common'

dayjs.extend(utc)

dayjs.extend(duration)

dayjs.extend(customParseFormat)

const { cookies } = useCookies()

export const arrayMove = <T>(array: T[], fromIndex: number, toIndex: number) => {
  // Может изменять исходный массив, т.к. не глубокое копирование. В случае чего можно заменить на structuredClone
  const newArray = [...array]

  const [movedItem] = newArray.splice(fromIndex, 1)

  newArray.splice(toIndex, 0, movedItem)

  return newArray
}

export const getViewportWidth = () => Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)

export const findParent = (el: HTMLElement, selector: string) => {
  if (el.closest) {
    return el.closest(selector)
  }

  let parent: HTMLElement | null = el

  while (parent) {
    if (parent.matches(selector)) {
      return parent
    }

    parent = parent.parentElement
  }

  return null
}

export const isDelayExceeded = (dateName: string, delay: number, isCookie = false) => {
  const timeStamp = isCookie ? +cookies.get(dateName) : +(localStorage.getItem(dateName) ?? 0)

  return new Date().getTime() - timeStamp >= delay * 0.95
}

export const format = (num: number, customFractionPart: number | undefined = undefined) => {
  if (Number.isNaN(+num)) return ''

  let fractionPart: number

  if (customFractionPart || customFractionPart === 0) {
    fractionPart = customFractionPart
  } else if (GLOBALS.COUNTRY_CODE === 'BY') {
    fractionPart = 3
  } else {
    fractionPart = 2
  }

  if (!Number.isInteger(num)) {
    const [_, fraction] = num.toString().split('.')
    const fractionDigitsPart = +fraction.slice(0, fractionPart)

    if (GLOBALS.COUNTRY_CODE === 'BY' && (fraction.length <= 2 || fraction[2] === '0')) {
      fractionPart = 2
    }

    fractionPart = fractionDigitsPart === 0 ? 0 : fractionPart
  } else {
    fractionPart = 0
  }

  if (Math.abs(num) >= 1000 && Math.abs(num) < 10000) {
    return num.toFixed(fractionPart)
  }

  const options = {
    minimumFractionDigits: fractionPart,
    maximumFractionDigits: fractionPart,
  }

  return new Intl.NumberFormat('ru-RU', options).format(num).replace(',', '.')
}

export const removeDateTimeZone = (date: string) => date.match(/\d+-\d+-\d+/g)

export const isToday = (date: string): boolean => dayjs().isSame(date, 'day')

export const isScrollable = (selector: string = '#cabinet-content-front') => {
  const el = document.querySelector(selector)

  if (!el) {
    return false
  }

  return el.scrollHeight > window.innerHeight
}

export const isMobile = (screenResolution: number) => screenResolution >= window.innerWidth

export const formatPhoneNumber = (
  phoneNumber: string | null,
  pattern: CountryMasks[CountryCode] = countryMasks[COUNTRY_CODE],
) => {
  if (!phoneNumber || !pattern) {
    return ''
  }

  const cleaned = phoneNumber.replace(/\D/g, '')
  const cleanedPattern = pattern.replace(/\D/g, '')

  if (cleaned.length !== cleanedPattern.length) {
    return ''
  }

  let patternIndex = 0
  let numberIndex = 0
  let formattedNumber = ''

  while (patternIndex < pattern.length && numberIndex < cleaned.length) {
    if (!pattern[patternIndex].includes(' ') && Number.isInteger(+pattern[patternIndex])) {
      formattedNumber += cleaned[numberIndex]

      numberIndex += 1
    } else {
      formattedNumber += pattern[patternIndex]
    }

    patternIndex += 1
  }

  return formattedNumber
}

export const formatPhoneWithFallback = (phoneNumber: string | null): string => {
  if (!phoneNumber) return ''

  for (const mask of Object.values(countryMasks)) {
    const formatted = formatPhoneNumber(phoneNumber, mask)

    if (formatted) return formatted
  }

  return phoneNumber
}

export const secondsToTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60) // без округления

  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

export const minutesToISOString = (minutes: number) => {
  const timeDuration = dayjs.duration(minutes, 'minutes')

  return dayjs.utc().startOf('day').add(timeDuration).format('HH:mm:ss')
}

export const numMinutesToHHmm = (minutes: number) => {
  return dayjs().startOf('day').add(minutes, 'minute').format('HH:mm')
}

export const HHmmToNumMinutes = (time: string) => {
  const normalized = time.length === 5 ? `${time}:00` : time
  const parsed = dayjs(normalized, 'HH:mm:ss', true)

  return parsed.isValid() ? parsed.diff(dayjs().startOf('day'), 'minute') : 0
}

export const replaceAt = (str: string, indexStart: number, indexEnd: number, replacement: string) => {
  if (indexStart >= str.length) return str

  return `${str.substring(0, indexStart)}${replacement}${str.substring(indexEnd)}`
}

export const formatDateToDDMMMM = (date: string) => {
  const { t } = useI18n()

  if (dayjs(date).isSame(dayjs(), 'day')) {
    return `${t('campaign.today')} (${dayjs(date).format('DD MMMM')})`
  }

  if (dayjs(date).isSame(dayjs().subtract(1, 'year'), 'year')) {
    return `${dayjs(date).format('DD MMMM YYYY')}`
  }

  return `${dayjs(date).format('DD MMMM')}`
}

export const formatDate = (isoDate: string, timezone?: string) => {
  const { t } = useI18n()
  const months = [
    t('generic.january'),
    t('generic.february'),
    t('generic.march'),
    t('generic.april'),
    t('generic.may'),
    t('generic.june'),
    t('generic.july'),
    t('generic.august'),
    t('generic.september'),
    t('generic.october'),
    t('generic.november'),
    t('generic.december'),
  ]

  const baseDate = new Date(isoDate)
  const date = timezone ? getUserTimezoneDate(baseDate, timezone) : baseDate

  const day = date.getDate()
  const month = months[date.getMonth()]
  const year = date.getFullYear()

  return `${day} ${month} ${year}`
}

export const formatDateToHHmm = (isoDate: string, timezone?: string) => {
  const baseDate = new Date(isoDate)
  const date = timezone ? getUserTimezoneDate(baseDate, timezone) : baseDate

  return dayjs(date).format('HH:mm')
}

export const pluralizeTime = (
  value: number,
  unit: 'second' | 'minute' | 'hour' | 'once',
  withValue = false,
  caseType: 'nominative' | 'accusative' = 'nominative',
) => {
  const { t } = useI18n()

  const cases = {
    second: {
      nominative: [t('generic.second_time'), t('generic.second_time_many'), t('generic.second_time_many_other')],
      accusative: [t('generic.second_time_other'), t('generic.second_time_many'), t('generic.second_time_many_other')],
    },
    minute: {
      nominative: [t('generic.minute_time'), t('generic.minute_time_many'), t('generic.minute_time_many_other')],
      accusative: [t('generic.minute_time_other'), t('generic.minute_time_many'), t('generic.minute_time_many_other')],
    },
    hour: {
      nominative: [t('generic.hour_time'), t('generic.hour_time_many'), t('generic.hour_time_many_other')],
      accusative: [t('generic.hour_time'), t('generic.hour_time_many'), t('generic.hour_time_many_other')],
    },
    once: {
      nominative: [t('generic.times_short'), t('generic.times_short_many'), t('generic.times_short')],
      accusative: [t('generic.times_short'), t('generic.times_short_many'), t('generic.times_short')],
    },
  }

  const getPluralForm = (n: number) => {
    const nAbs = Math.abs(n) % 100
    const nLast = nAbs % 10

    if (nAbs > 10 && nAbs < 20) return 2

    if (nLast > 1 && nLast < 5) return 1

    if (nLast === 1) return 0

    return 2
  }

  const caseIndex = getPluralForm(value)

  const pluralForm = `${cases[unit][caseType][caseIndex]}`

  return withValue ? `${value} ${pluralForm}` : pluralForm
}

export const isValidEmail = (email: string) => {
  return /^[a-zA-Z0-9._-]+@[\w\-.][-\w]*\.[\w\-.]+$/.test(email)
}

export const isValidUrl = (url: string) => {
  if (url.includes('|')) return false

  return /^https?:[\\/]*[^\\/]/i.test(url)
}

export const isTouchscreenDevice =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|BB|PlayBook|IEMobile|Windows Phone|Kindle|Silk|Opera Mini/i.test(
    navigator.userAgent,
  )

export const sendGTMEvent = (value: string, event = 'event') => {
  if (typeof dataLayer !== 'undefined' && GLOBALS.ENVIRONMENT === 'prod') {
    dataLayer?.push({ [event]: value })
  }
}

export const getStaticImagePath = (pathName: string) => `/manager/static/${pathName}`

export const getPostbackGuideUrl = (isCallo: boolean, locale: string): string =>
  isCallo ? `https://callo.com/${locale}/guides/postbacks/` : `https://zvonok.com/${locale}/guide/guide_postback/`

export const isOutside = (target: Element, selector: string) =>
  target.classList.contains(selector) || target.closest(selector)

export const isUsedWord = (word = '', usedWords: string[]) => {
  let formattedWord = ''

  const lowerUsedWords = usedWords.map(word => word.toLowerCase())

  if (/[—–−-]/.test(word)) {
    formattedWord = word.replace(/[—–−-]/g, ' ')
  }

  return lowerUsedWords.includes(formattedWord.toLowerCase() || word.toLowerCase())
}

export const getNumberWithSuffix = (number: number) => {
  const { t } = useI18n()

  if (!Number.isInteger(number) || number < 0) return ''

  const lastDigit = number % 10
  const lastTwoDigits = number % 100

  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return t('generic.ordinals.default', { number })
  }

  if (number === 0) {
    return t('generic.ordinals.zero', { number })
  }

  switch (lastDigit) {
    case 1:
      return t('generic.ordinals.one', { number })
    case 2:
      return t('generic.ordinals.two', { number })
    case 3:
      return t('generic.ordinals.three', { number })
    case 6:
    case 7:
    case 8:
      return t('generic.ordinals.six_seven_eight', { number })
    default:
      return t('generic.ordinals.default', { number })
  }
}

export const getFilenameFromUrl = (url: string) => {
  try {
    const pathname = new URL(url, window.location.href).pathname

    return pathname.split('/').pop() || 'file'
  } catch {
    return 'file'
  }
}

export const withExtFromUrl = (name: string, url: string) => {
  const urlFilename = getFilenameFromUrl(url)
  const dotIndex = urlFilename.lastIndexOf('.')
  const ext = dotIndex !== -1 ? urlFilename.slice(dotIndex) : ''

  if (!name) return urlFilename

  return ext && !name.endsWith(ext) ? `${name}${ext}` : name
}

export const isSameOrigin = (url: string) => {
  try {
    return new URL(url, window.location.href).origin === window.location.origin
  } catch {
    return false
  }
}

export const downloadFile = async (fileUrl: string, fileName = 'file', fetchOption = true) => {
  try {
    const link = document.createElement('a')

    link.download = fileName

    if (fetchOption) {
      const response = await fetch(fileUrl)
      const contentType = response.headers.get('content-type') ?? ''
      const isHtml = contentType.includes('text/html')

      if (response.ok && !isHtml) {
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)

        link.href = url

        link.download = fileName

        document.body.appendChild(link)

        link.click()

        document.body.removeChild(link)

        URL.revokeObjectURL(url)

        return
      }
    }

    link.href = fileUrl

    link.target = '_blank'

    document.body.appendChild(link)

    link.click()

    document.body.removeChild(link)
  } catch (err) {
    console.error(err)
  }
}

export const downloadFileFromClient = (file: File) => {
  const url = URL.createObjectURL(file)
  const a = document.createElement('a')

  a.href = url

  a.download = file.name // имя файла в сохранении

  document.body.appendChild(a)

  a.click()

  document.body.removeChild(a)

  URL.revokeObjectURL(url) // очистка памяти
}

export const bytesToSize = (bytes: number, precision = 2) => {
  const { t } = useI18n()

  if (bytes === 0) return `0 ${t('generic.byte')}`
  const units = [
    t('generic.byte'),
    t('generic.kilobyte.short'),
    t('generic.megabyte.short'),
    t('generic.gigabyte.short'),
  ]
  const index = Math.floor(Math.log(bytes) / Math.log(1024))

  return `${(bytes / 1024 ** index).toFixed(precision)} ${units[index]}`
}

export const debounce = <T extends (...args: Parameters<T>) => void>(func: T, delay: number) => {
  let timerId: ReturnType<typeof setTimeout>

  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    clearTimeout(timerId)

    timerId = setTimeout(() => func.apply(this, args), delay)
  }
}

export const sleep = async (delay: number): Promise<void> => {
  return new Promise(resolve => {
    const id = setTimeout(() => {
      clearTimeout(id)

      resolve()
    }, delay)
  })
}

export const formatDateToDDMMYY = (isoDate: string) => {
  return dayjs(isoDate).format('DD.MM.YY')
}

export const formatDateToDDMMYYYY = (isoDate: string) => {
  return dayjs(isoDate).format('DD.MM.YYYY')
}

export const formatSecondsToMMSS = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  const paddedSeconds = seconds.toString().padStart(2, '0')

  return `${minutes}:${paddedSeconds}`
}

const escapeRegExp = (string: string) => {
  return string.replace(/[\-[\]/{}()*+?.\\^$|]/g, '\\$&')
}

export const countSMSParts = (value: string) => {
  const textLength = value.length

  let oneSmsLength
  let multipleSmsLength
  let parts

  const bit7ValuesStr = escapeRegExp(
    '%@Δ0¡P¿p£_!1AQaq$Φ"2BRbr¥Γ#3CScsèΛ¤4DTdtéΩ5EUeuùΠ&6FVfvìΨ\'7GWgwòΣ(8HXhxÇΘ)9IYiyΞ*:JZjzØ+;KÄkäøÆ,<LÖlöæ-=MÑmñÅß.>NÜnüåÉ/?O§oà\r\n\x1B ',
  )

  const unicodeSmsPattern = new RegExp(`([^${bit7ValuesStr}])`)

  if (unicodeSmsPattern.test(value.trim())) {
    oneSmsLength = 70

    multipleSmsLength = 67
  } else {
    oneSmsLength = 160

    multipleSmsLength = 153
  }

  if (textLength <= oneSmsLength) {
    parts = 1
  } else {
    parts = Math.ceil(textLength / multipleSmsLength)
  }

  return parts
}

export const getUserTimezoneDate = (date: Date, timezone?: string) => {
  const safeTZ = timezone || new Intl.DateTimeFormat().resolvedOptions().timeZone
  // Формируем строку с датой и временем в нужной таймзоне
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone: safeTZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  const parts = f.formatToParts(date).reduce(
    (acc, p) => {
      if (p.type !== 'literal') acc[p.type] = p.value

      return acc
    },
    {} as Record<string, string>,
  )

  // Конструируем объект Date в локальной таймзоне
  return new Date(`${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`)
}

export const startOfDayInTZ = (date: Date, timezone: string) => {
  const d = getUserTimezoneDate(date, timezone)

  d.setHours(0, 0, 0, 0)

  return d
}

export const endOfDayInTZ = (date: Date, timezone: string) => {
  const d = getUserTimezoneDate(date, timezone)

  d.setHours(23, 59, 59, 999)

  return d
}

export const convertLocale = (locale: string): string => locale.split('_')[0]

export const getOperatorIcon = (operator: string): IconName | null => {
  if (operator in operatorsData) return operatorsData[operator as Operators].icon

  return null
}

export const getCallerOperatorIcon = (operator: string | null | undefined): IconName | null => {
  if (!operator) return null

  const slug = operator.split('_')[0]

  return getOperatorIcon(slug === 'beeline' ? 'bln' : slug)
}

export const getOperatorName = (operator: string, t: TranslateFunction): string | null => {
  if (operator in operatorsData) return t(operatorsData[operator as Operators].nameKey)

  return null
}

/**
 * Добавляет +1 микросекунду
 *
 * @returns 'YYYY-MM-DDTHH:mm:ss.sssZ'
 * @param isoDate
 */
export const addOneMicrosecond = (isoDate: string) => {
  const match = isoDate.match(/^(.+):(\d{2})\.(\d{6})Z$/)

  if (!match) return

  const [, datePart, seconds, micro] = match
  const microNum = Number(micro)

  // Если можно просто увеличить микросекунду
  if (microNum < 999999) {
    const nextMicro = (microNum + 1).toString().padStart(6, '0')

    return `${datePart}:${seconds}.${nextMicro}Z`
  }

  // Если микросекунды переполняются → +1 секунда
  const dateWithoutMicro = `${datePart}:${seconds}Z`
  const date = new Date(dateWithoutMicro)

  date.setUTCSeconds(date.getUTCSeconds() + 1)

  const iso = date.toISOString()
  const [base] = iso.split('.')

  return `${base}.000000Z`
}

/**
 * Возвращает дату с микросекундами
 * На выходе получаем время в UTC, относительно GLOBALS.USER.TIMEZONE
 *
 * @returns 'YYYY-MM-DDTHH:mm:ss.sssZ'
 * @param input
 */
export const toIsoWithMicroZ = (input: Date | string | number) => {
  const userZone = GLOBALS?.USER?.TIMEZONE || dayjs.tz.guess()

  const rawLocalTime = dayjs(input).format('YYYY-MM-DD HH:mm:ss')

  const utcDate = dayjs.tz(rawLocalTime, userZone).utc()

  if (!utcDate.isValid()) return

  return utcDate.format('YYYY-MM-DD[T]HH:mm:ss.SSS[000Z]')
}
